import React, { useState, useEffect, useCallback } from 'react';
import LocalFilerRecord from '../dataObjects/LocalFilerRecord';
import UnfiledReport from '../dataObjects/UnfliledReport';
import EditOrAddNewFilerRecord from './EditOrAddNewFilerRecord';
import FilerRecordsTable from './FilerRecordsTable';
import EditOrAddNewUnfiledReport from './EditOrAddNewUnfiledReport';
import dayjs, {Dayjs} from 'dayjs';
import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';


import { Switch, FormControlLabel, AppBar, Button, Toolbar, Box, Typography, Grid, CssBaseline, ButtonBase } from '@mui/material/';
import ExtranetLogo from '../../../components/nav/ExtranetLogo';
import '../view-nonfiler-tracker-styles.css';


function LocalFilersWithUnfiledReports() {

    const [localFilersWithUnfiledReports, setLocalFilersWithUnfiledReports] = useState<LocalFilerRecord[]>([]);
    const [openFilerDialog, setOpenFilerDialog] = useState(false);
    const [openReportDialog, setOpenReportDialog] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<LocalFilerRecord | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [openUnfiledReportDialog, setOpenUnfiledReportDialog] = useState(false);
    const [currentUnfiledReport, setCurrentUnfiledReport] = useState<UnfiledReport | null>(null);
    const [isArchived, setIsArchived] = useState<boolean>(false);


    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');

    const [errors, setErrors] = useState({
        town: { error: false, helperText: "" }
        // Add more fields as necessary...
    });

    const handleIsArchivedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsArchived(event.target.checked);
        //props.handleFilter({ isArchived: event.target.checked });
    };

    const getLocalFilersWithUnfiledReports = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                IsArchived: (+isArchived).toString(),
                SortField: sortField,
                SortDirection: sortDirection
            });

            const filersUrl = `/NonfilerTracker/GetFilersWithUnfiledReports?${params}`;
            const filersResponse = await fetch(filersUrl);
            const filersData: { result: LocalFilerRecord[] } = await filersResponse.json();

            if (!Array.isArray(filersData.result) || filersData.result.length === 0) {
                console.warn("No filer data available or the array is empty.");
                setLocalFilersWithUnfiledReports([]);
                return;
            }

            const filersWithReports = await Promise.all(filersData.result.map(async (filer: LocalFilerRecord) => {
                const reportsUrl = `/NonfilerTracker/GetFilersUnfiledReports?localFilerRecordId=${filer.localFilerRecordId}`;
                const reportsResponse = await fetch(reportsUrl);
                const reportsData: { result: UnfiledReport[] } = await reportsResponse.json();

                return {
                    ...filer,
                    unfiledReports: reportsData.result
                };
            }));

            setLocalFilersWithUnfiledReports(filersWithReports);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [sortField, sortDirection, isArchived]);

    useEffect(() => {
        getLocalFilersWithUnfiledReports();
    }, [getLocalFilersWithUnfiledReports]);


    const handleOpenFilerDialog = (record: LocalFilerRecord | null = null, editing: boolean = false) => {
        setCurrentRecord(record || {
            localFilerRecordId: 0,
            town: '',
            candidateName: '',
            candidateAddress: '',
            candidateCsz: '',
            candidateSalutation: '',
            notes: '',
            notifiedByClerk: '',
            letterSent: '',
            filedDate: '',
            closedDate: '',
            referralDate: '',
            isArchived: false,
            unfiledReports: []
        });
        setIsEditing(editing);
        setOpenFilerDialog(true);
    };

    const handleOpenReportDialog = (record: LocalFilerRecord | null = null, editing: boolean = false) => {
        setCurrentRecord(record || {
            localFilerRecordId: 0,
            town: '',
            candidateName: '',
            candidateAddress: '',
            candidateCsz: '',
            candidateSalutation: '',
            notes: '',
            notifiedByClerk: '',
            letterSent: '',
            filedDate: '',
            closedDate: '',
            referralDate: '',
            isArchived: false,
            unfiledReports: []
        });
        setIsEditing(editing);
        setOpenReportDialog(true);
    };

    const handleCloseFilerDialog = () => {
        setOpenFilerDialog(false);
        setCurrentRecord(null);
    };

    const handleCloseReportDialog = async () => {
        getLocalFilersWithUnfiledReports();
        setOpenReportDialog(false);
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentRecord(prev => prev ? { ...prev, [name]: value } : null);
    };

    const handleDateChange = (fieldName: string) =>
        (value: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => {
            const formattedDate = value ? value.format('MM/DD/YYYY') : '';

            setCurrentRecord(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    [fieldName]: formattedDate
                };
            });
        };

    const handleSaveFiler = async () => {
        if (currentRecord && !validateFields()) {
            try {
                var params = [
                    `Id=${currentRecord.localFilerRecordId}`,
                    `Town=${currentRecord.town}`,
                    `CandidateName=${currentRecord.candidateName}`,
                    `CandidateAddress=${currentRecord.candidateAddress}`,
                    `CandidateCsz=${currentRecord.candidateCsz}`,
                    `CandidateSalutation=${currentRecord.candidateSalutation}`,
                    `NotifiedByClerk=${currentRecord.notifiedByClerk}`,
                    `LetterSent=${currentRecord.letterSent}`,
                    `FiledDate=${currentRecord.filedDate}`,
                    `ClosedDate=${currentRecord.closedDate}`,
                    `ReferralDate=${currentRecord.referralDate}`,
                    `IsArchived=${currentRecord.isArchived}`,
                    `Notes=${currentRecord.notes}`
                ].join('&');
                const url = `/NonfilerTracker/SaveFilersWithUnfiledReports?${params}`;
                const method = 'POST';

                const response = await fetch(url, {
                    method: method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(currentRecord)
                });

                if (response.ok) {
                    getLocalFilersWithUnfiledReports();  
                    handleCloseFilerDialog();
                } else {
                    console.error('Failed to save record');
                }
            } catch (error) {
                console.error('Error saving record:', error);
            }
        }
    };

    const handleDelete = async (localFilerRecordId: number) => {
        try {
            const response = await fetch(`/NonfilerTracker/DeleteFilerWithUnfiledReports?localFilerRecordId=${localFilerRecordId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                getLocalFilersWithUnfiledReports();
            } else {
                console.error('Failed to delete record');
            }
        } catch (error) {
            console.error('Error deleting record:', error);
        }

    };

    const handleArchive = async (localFilerRecordId: number) => {
        try {
            const response = await fetch(`/NonfilerTracker/ArchiveFilerWithUnfiledReports?localFilerRecordId=${localFilerRecordId}`, {
                method: 'POST'
            });

            if (response.ok) {
                getLocalFilersWithUnfiledReports();
            } else {
                console.error('Failed to archive record');
            }
        } catch (error) {
            console.error('Error archiving record:', error);
        }

    };

    const handleRefer = async (name: string, localFilerId: number) => {
        //save new litigation record 
        const params = [
            `name=${name}`,
            `localFilerId=${localFilerId}`,
        ].join('&');
        const url = `/NonfilerTracker/ReferToLitigation?${params}`;
        const method = 'POST';

        const response = await fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(currentRecord)
        });

        if (response.ok) {
            alert('Reffered!')
        } else {
            console.error('Failed to save record');
        }
    };

    const handleOpenUnfiledReportDialog = (report: UnfiledReport | null = null) => {
        setCurrentUnfiledReport(report || {
            unfiledReportId: 0,
            localFilerRecordId: currentRecord!.localFilerRecordId,
            reportTypeId: 0,
            reportTypeDescription: '',
            year: '',
            reportStatusId: 0,
            reportStatusDescription: ''
        });
        setOpenUnfiledReportDialog(true);
    };

    const handleCloseUnfiledReportDialog = () => {
        setOpenUnfiledReportDialog(false);
        setCurrentUnfiledReport(null);
    };


    const handleUnfiledReportInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentUnfiledReport(prev => ({ ...prev!, [name]: value }));
    };

    const handleSaveUnfiledReport = async () => {
        if (currentUnfiledReport && currentRecord) {
            try {
                var params = [
                    `unfiledReportId=${currentUnfiledReport.unfiledReportId}`,
                    `localFilerRecordId=${currentUnfiledReport.localFilerRecordId}`,
                    `reportTypeId=${currentUnfiledReport.reportTypeId}`,
                    `year=${currentUnfiledReport.year}`,
                    `reportTypeId=${currentUnfiledReport.reportStatusId}`
                ].join('&');
                const url = `/NonfilerTracker/SaveFilersUnfiledReport?${params}`;
                const method = 'POST';

                const response = await fetch(url, {
                    method: method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(currentUnfiledReport)
                });

                if (response.ok) {
                    /*issue here?*/
                    console.log(currentUnfiledReport);

                    // If the unfiled report already exists, update it. Otherwise, add it to the list.
                    const updatedUnfiledReports =
                        currentUnfiledReport.unfiledReportId ?
                            currentRecord.unfiledReports.map(report =>
                                report.unfiledReportId === currentUnfiledReport.unfiledReportId ? currentUnfiledReport : report
                            ) :
                            [...currentRecord.unfiledReports, currentUnfiledReport];



                    setCurrentRecord({ ...currentRecord, unfiledReports: updatedUnfiledReports });
                    ; handleCloseUnfiledReportDialog();

                } else {
                    console.error('Failed to save unfiled report');
                }
            } catch (error) {
                console.error('Error saving unfiled report:', error);
            }
        }
    };

    const handleDeleteUnfiledReport = async (unfiledReportId: number) => {
        if (currentRecord) {
            try {
                const response = await fetch(`/NonfilerTracker/DeleteFilersUnfiledReport?unfiledReportId=${unfiledReportId}`, {
                    method: 'DELETE'
                });

                if (response.ok) {
                    const updatedUnfiledReports = currentRecord.unfiledReports.filter(
                        report => report.unfiledReportId !== unfiledReportId
                    );
                    setCurrentRecord({ ...currentRecord, unfiledReports: updatedUnfiledReports });
                } else {
                    console.error('Failed to delete unfiled report');
                }
            } catch (error) {
                console.error('Error deleting unfiled report:', error);
            }
        }
    };

    const handleSort = (field: string) => {
        setSortField(field);
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };


    // Validation function
    const validateFields = () => {
        let tempErrors = { ...errors }; // Clone the current errors state

        // Check for 'name' validation
        if (!currentRecord?.town) {
            tempErrors.town = {
                error: true,
                helperText: "Town is required",
            };
        } else {
            tempErrors.town = { error: false, helperText: "" };
        }

        // Update the error state
        setErrors(tempErrors);

        // Return whether there are any errors
        return Object.values(tempErrors).some(field => field.error);
    };


    return (
        <div>

        <FormControlLabel  
          sx={{  
              marginLeft: 'auto',  
              marginRight: 5,  
              marginTop: 2,  
              marginBottom: 2,  
              display: 'flex',  
              justifyContent: 'flex-end'  
          }}  
          control={  
              <Switch  
                  checked={isArchived}  
                  onChange={handleIsArchivedChange}  
                  name="isArchived"  
              />  
          }  
          label='Archived?'  
        />

            <FilerRecordsTable
                localFilersWithUnfiledReports={localFilersWithUnfiledReports}
                handleOpenFilerDialog={handleOpenFilerDialog}
                handleOpenReportDialog={handleOpenReportDialog}
                handleDelete={handleDelete}
                handleArchive={handleArchive}
                getLocalFilersWithUnfiledReports={getLocalFilersWithUnfiledReports}
                handleSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                handleRefer={handleRefer}
                isArchived={isArchived}
            />

            <Button onClick={() => handleOpenFilerDialog()} variant="contained" color="primary"
                sx={{
                    margin: 0,
                    top: 'auto',
                    right: 20,
                    bottom: 20,
                    left: 'auto',
                    position: 'fixed'
                }}>Add New Filer Record</Button>



            <EditOrAddNewFilerRecord
                currentRecord={currentRecord}
                isEditing={isEditing}
                openDialog={openFilerDialog}
                handleCloseDialog={handleCloseFilerDialog}
                handleInputChange={handleInputChange}
                handleDateChange={handleDateChange}
                handleSave={handleSaveFiler}
                errors={errors}
            />

            <EditOrAddNewUnfiledReport
                currentUnfiledReport={currentUnfiledReport}
                currentRecord={currentRecord}
                openUnfiledReportDialog={openUnfiledReportDialog}
                isEditing={isEditing}
                isLitigationTab={false}
                openDialog={openReportDialog}
                handleCloseDialog={handleCloseReportDialog}
                handleOpenUnfiledReportDialog={handleOpenUnfiledReportDialog}
                handleDeleteUnfiledReport={handleDeleteUnfiledReport}
                handleCloseUnfiledReportDialog={handleCloseUnfiledReportDialog}
                handleUnfiledReportInputChange={handleUnfiledReportInputChange}
                handleSaveUnfiledReport={handleSaveUnfiledReport}
            />



        </div>
    );
}

export default LocalFilersWithUnfiledReports;